import { KbDocumentsPanel } from "@/components/authoring/kb-documents-panel";
import { ReturnBanner } from "@/components/return-banner";
import { useReturnTo } from "@/lib/return-to";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Pencil, Play, RefreshCw, Sparkles } from "lucide-react";
import { Link } from "@/components/ui/link";
import { useParams } from "@/lib/navigation";
import * as React from "react";

import { KbEditDialog } from "@/components/authoring/kb-edit-dialog";
import { KbStatusBadge } from "@/components/authoring/kb-status-badge";
import { PageHeader } from "@/components/layout-bits";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Skeleton,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
  Textarea,
} from "@/components/ui/primitives";
import { ErrorState } from "@/components/ui/states";
import { toast } from "@/components/ui/toaster";
import { ingestKnowledgeBase, queryKnowledgeBase } from "@/lib/api/endpoints";
import { formatTokens, formatUsd } from "@/lib/format-usage";
import type { KbPassage, KbQueryUsage } from "@/types/api";
import { useKnowledgeBase } from "@/lib/query/hooks";
import { qk } from "@/lib/query/keys";
import type { KbQueryResult } from "@/types/api";

export default function KnowledgeBaseDetailPage() {
  const returnTo = useReturnTo();
  const params = useParams();
  const slug = String(params.slug);
  const qc = useQueryClient();
  const kb = useKnowledgeBase(slug);

  const ingest = useMutation({
    mutationFn: () => ingestKnowledgeBase(slug),
    onSuccess: async () => {
      toast.success("Ingestion started — this may take a moment.");
      await qc.invalidateQueries({ queryKey: qk.knowledgeBase(slug) });
      await qc.invalidateQueries({ queryKey: qk.knowledgeBases });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const [editOpen, setEditOpen] = React.useState(false);
  const [question, setQuestion] = React.useState("");
  const [answer, setAnswer] = React.useState<KbQueryResult | undefined>();
  const ask = useMutation({
    mutationFn: () => queryKnowledgeBase(slug, question.trim()),
    onSuccess: (r) => setAnswer(r),
    onError: (e: Error) => toast.error(e.message),
  });

  if (kb.isError) {
    return <ErrorState error={kb.error} onRetry={() => kb.refetch()} />;
  }

  const data = kb.data;
  const isReady = data?.status === "ready";
  const isIngesting = data?.status === "ingesting";

  return (
    <div className="mx-auto max-w-4xl">
      <Link
        href="/knowledge-bases"
        className="mb-2 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Knowledge Base
      </Link>

      {/* Present when an agent version sent the admin here to set up a KB.
          This is where ingestion happens, so this — not the create dialog — is
          the point at which going back actually makes sense. */}
      {returnTo ? <ReturnBanner to={returnTo} noun="a knowledge base" /> : null}

      {kb.isLoading || !data ? (
        <Skeleton className="h-96 w-full" />
      ) : (
        <>
          <PageHeader
            title={data.name}
            description={
              data.source_type === "document"
                ? "Document knowledge base — answers are grounded in your files, with citations."
                : `Text-to-SQL knowledge base over your ${data.source_type.toUpperCase()} source.`
            }
            actions={
              <div className="flex items-center gap-3">
                <KbStatusBadge status={data.status} />
                <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
                  <Pencil className="h-4 w-4" />
                  Edit
                </Button>
              </div>
            }
          />

          <div className="space-y-6">
            {/* --- Overview --- */}
            <Card>
              <CardHeader className="flex-row items-center justify-between space-y-0">
                <CardTitle className="text-base">Configuration</CardTitle>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => ingest.mutate()}
                  disabled={ingest.isPending || isIngesting}
                >
                  {isIngesting ? (
                    <>
                      <RefreshCw className="h-4 w-4 animate-spin" /> Ingesting…
                    </>
                  ) : (
                    <>
                      <RefreshCw className="h-4 w-4" />
                      {data.source_type === "document"
                        ? isReady
                          ? "Re-sync documents"
                          : "Ingest documents"
                        : isReady
                          ? "Re-ingest schema"
                          : "Ingest schema"}
                    </>
                  )}
                </Button>
              </CardHeader>
              <CardContent>
                <dl className="grid gap-x-8 gap-y-4 text-sm sm:grid-cols-2">
                  <Field label="Domain">{data.domain_slug}</Field>
                  <Field label="Embedding model">
                    <span className="font-mono text-xs">
                      {data.embedding_model_name ?? "—"}
                      {data.embedding_model ? ` / ${data.embedding_model}` : ""}
                      {data.embedding_dim ? ` (${data.embedding_dim}d)` : ""}
                    </span>
                  </Field>
                  <Field label="Vector store">
                    <span className="font-mono text-xs">
                      {data.vector_store_name ?? "—"}
                      {data.vector_store_provider ? ` (${data.vector_store_provider})` : ""} ·{" "}
                      {data.vector_collection}
                    </span>
                  </Field>
                  <Field label="Objects embedded">
                    <span className="tabular-nums">{data.object_count}</span>
                  </Field>
                  <Field label="Attached to">
                    <span className="tabular-nums">{data.attached_count} agent version(s)</span>
                  </Field>
                  <Field label="Last synced">
                    {data.last_synced_at ? new Date(data.last_synced_at).toLocaleString() : "Never"}
                  </Field>
                  <div className="sm:col-span-2">
                    <dt className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      Allowed tables
                    </dt>
                    <dd className="flex flex-wrap gap-1.5">
                      {data.allowed_tables && data.allowed_tables.length > 0 ? (
                        data.allowed_tables.map((t) => (
                          <Badge key={t} variant="outline" className="font-mono text-[10px]">
                            {t}
                          </Badge>
                        ))
                      ) : (
                        <span className="text-muted-foreground">All tables</span>
                      )}
                    </dd>
                  </div>
                </dl>

                {data.status === "failed" && data.last_error ? (
                  <div className="mt-4 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-xs text-destructive">
                    <span className="font-semibold">Last ingestion failed:</span> {data.last_error}
                  </div>
                ) : null}
              </CardContent>
            </Card>

            {/* Documents live only on a document KB; a SQL KB has tables, not
                files, and showing an empty documents table there would imply a
                capability it does not have. */}
            {data.source_type === "document" ? (
              <div className="mt-6">
                <KbDocumentsPanel slug={slug} />
              </div>
            ) : null}

            {/* --- Query console --- */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Sparkles className="h-4 w-4 text-accent" />
                  Ask a question
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {!isReady ? (
                  <p className="text-sm text-muted-foreground">
                    Ingest the schema first — the knowledge base must be{" "}
                    <span className="font-medium">ready</span> before you can query it.
                  </p>
                ) : null}
                <form
                  className="space-y-3"
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (isReady && question.trim()) ask.mutate();
                  }}
                >
                  <Textarea
                    rows={2}
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                    placeholder={
                      data.source_type === "document"
                        ? "e.g. What is the holiday allowance?"
                        : "e.g. How many orders were placed last month?"
                    }
                    disabled={!isReady}
                  />
                  <div className="flex justify-end">
                    <Button type="submit" disabled={!isReady || !question.trim() || ask.isPending}>
                      <Play className="h-4 w-4" />
                      {ask.isPending ? "Thinking…" : "Run"}
                    </Button>
                  </div>
                </form>

                {answer?.usage ? <QueryUsage usage={answer.usage} /> : null}

                {answer && data.source_type === "document" ? (
                  <PassageResults passages={answer.passages ?? []} />
                ) : answer ? (
                  <div className="space-y-3">
                    <div>
                      <div className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                        Generated SQL
                      </div>
                      <pre className="overflow-x-auto rounded-md border border-border bg-muted/40 p-3 font-mono text-xs">
                        {answer.sql}
                      </pre>
                    </div>
                    <div>
                      <div className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                        {answer.row_count} row{answer.row_count === 1 ? "" : "s"}
                      </div>
                      {answer.rows.length === 0 ? (
                        <p className="text-sm text-muted-foreground">No rows returned.</p>
                      ) : (
                        <div className="rounded-md border border-border">
                          <Table>
                            <THead>
                              <TR>
                                {answer.columns.map((c) => (
                                  <TH key={c}>{c}</TH>
                                ))}
                              </TR>
                            </THead>
                            <TBody>
                              {answer.rows.map((row, i) => (
                                <TR key={i}>
                                  {answer.columns.map((c) => (
                                    <TD key={c} className="font-mono text-xs">
                                      {formatCell(row[c])}
                                    </TD>
                                  ))}
                                </TR>
                              ))}
                            </TBody>
                          </Table>
                        </div>
                      )}
                    </div>
                  </div>
                ) : null}
              </CardContent>
            </Card>
          </div>

          <KbEditDialog open={editOpen} onOpenChange={setEditOpen} kb={data} />
        </>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="mb-0.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd className="text-foreground">{children}</dd>
    </div>
  );
}

/** What the test question cost — the same spend an agent's KB tool incurs per
 * call, so an operator can price a knowledge base before attaching it. */
function QueryUsage({ usage }: { usage: KbQueryUsage }) {
  return (
    <div className="flex flex-wrap items-center gap-x-5 gap-y-1 rounded-md border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
      <span>
        Embedding <span className="font-mono text-foreground">{usage.embedding_model ?? "—"}</span>{" "}
        · {formatTokens(usage.embedding_tokens)} tok ·{" "}
        <span className="tabular-nums text-foreground">{formatUsd(usage.embedding_cost_usd)}</span>
      </span>
      {usage.llm_model ? (
        <span>
          SQL generation <span className="font-mono text-foreground">{usage.llm_model}</span> ·{" "}
          {formatTokens(usage.llm_prompt_tokens)} in / {formatTokens(usage.llm_completion_tokens)}{" "}
          out ·{" "}
          <span className="tabular-nums text-foreground">{formatUsd(usage.llm_cost_usd)}</span>
        </span>
      ) : null}
      <span>
        Total{" "}
        <span className="font-medium tabular-nums text-foreground">
          {formatUsd(usage.total_cost_usd)}
        </span>
      </span>
      <span className="tabular-nums">{Math.round(usage.duration_ms)} ms</span>
    </div>
  );
}

function formatCell(value: unknown): string {
  if (value === null || value === undefined) return "∅";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

/** Query results for a document knowledge base.
 *
 * Passages rather than rows, each with the citation that names its source. The
 * console deliberately shows the citation *above* the text: when an operator is
 * checking whether the knowledge base is grounded in the right material, "which
 * document did this come from" is the question, and the passage is the evidence.
 */
function PassageResults({ passages }: { passages: KbPassage[] }) {
  if (passages.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No passages matched. Try rephrasing, or check that the documents you expect are indexed.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {passages.length} passage{passages.length === 1 ? "" : "s"}
      </div>
      {passages.map((p, i) => (
        <div key={`${p.filename}-${i}`} className="rounded-md border border-border p-3">
          <div className="mb-1.5 flex items-baseline justify-between gap-3">
            <span className="font-mono text-xs text-primary">{p.citation}</span>
            {/* The score is what makes a bad retrieval legible: a top hit at
                0.18 says the corpus does not cover the question, which reads
                very differently from a wrong-looking answer at 0.9. */}
            <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
              {p.score.toFixed(3)}
            </span>
          </div>
          <p className="whitespace-pre-wrap text-sm">{p.text}</p>
        </div>
      ))}
    </div>
  );
}
